import '.hx{min-width:360px;}.ssr-plugin'
import '.h{width:100%;}.ssr-plugin'
import '.c4{max-width:600px;}.ssr-plugin'
import '.hy{margin:96px auto;}.ssr-plugin'
import '.hz{margin-top:0px;}.ssr-plugin'
import '.i0{padding:0px 24px;}.ssr-plugin'
import {
  createElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useClient, useQuery } from '@based/sdk/react'
import { IconButton } from '../IconButton/index.js'
import { AppHeader } from '../AppHeader/index.js'
import { Text } from '../Text/index.js'
import { Card } from './Card.js'
import { IconProps } from '../Icon/index.js'
import { LanguageSelect } from '../LanguageSelect/index.js'
import { useBasedEndpoints } from '../../providers/Endpoints/index.js'
import {
  ContentEditor,
  ContentEditorProps,
  useContentEditor,
} from '../ContentEditor/index.js'
import { Button } from '../Button/index.js'
import { Dialog } from '../Dialog/index.js'
import { Stack } from '../Stack/index.js'
import { prettyDate } from '@based/pretty-date'
import { capitalize, normalize } from '../../utils/text.js'
import { useOverlay } from '../../hooks/useOverlay.js'
import { styled } from 'inlines'
import { ContextMenu } from '../ContextMenu/index.js'
import { useCmsParams } from '../../hooks/useCmsParams.js'
import { UiSchemaType } from '../../types/schema.js'
import { useDependency } from '../../hooks/useDependency.js'

export const Editor: React.FC<{
  schemaType?: string
  id?: number
  section?: string
  counter?: number
  include?: string[]
  exclude?: string[]
  disabled?: string[]
  icon?: IconProps['variant']
  leadButton?: ReactNode
  trailButton?: ReactNode
  languageSelector?: ReactNode
  isBackButtonHidden?: boolean
  headerVariant?: 'full' | 'mini'
  headerCustomTitle?: string
  customEndpoints?: Partial<ReturnType<typeof useBasedEndpoints>>
  showOpenPreview?: boolean
  hideContextMenu?: boolean
  changedFields?: (x: boolean) => void
  onSubmit?: ContentEditorProps['onSubmit']
  onChange?: ContentEditorProps['onChange']
  preview?: (props: Omit<Parameters<typeof Editor>[0], 'preview'>) => ReactNode
  banner?: (props: any) => ReactNode
  transformFields?: ContentEditorProps['transformFields']
}> = ({
  schemaType = '_root',
  id = 1,
  section,
  counter,
  icon,
  leadButton,
  trailButton,
  languageSelector,
  isBackButtonHidden = false,
  headerVariant,
  headerCustomTitle,
  include,
  exclude,
  disabled,
  customEndpoints,
  showOpenPreview = true,
  hideContextMenu,
  transformFields,
  changedFields,
  onSubmit,
  onChange,
  preview,
  banner,
}) => {
  const client = useClient()
  const [params, go] = useCmsParams()
  const endpoints = useBasedEndpoints()

  const customEndpointsDep = useDependency(customEndpoints)
  const endpointsMerged = useMemo(() => {
    // Keep endpoints stable; avoid deepCopy per render.
    if (!customEndpoints) return endpoints
    return { ...endpoints, ...customEndpoints }
  }, [endpoints, customEndpointsDep])

  const itemId = params.id ? Number(params.id) : id
  const itemName = section ? capitalize(section) : capitalize(schemaType)

  const { data: schema, checksum: schemaChecksum } = useQuery(
    endpointsMerged.schema,
  )

  const [lastUpdateTs, setLastUpdateTs] = useState<number | null>(null)
  const [isMultilanguage, setIsMultilanguage] = useState(false)

  const deleteOverlay = useOverlay(Dialog, {
    title: 'Delete item',
    description: 'Are you sure you want to permanently delete this item?',
    children: (
      <Stack gap={16}>
        <Button
          keyHint="Esc"
          variant="border"
          onClick={() => Dialog.Close()}
          width="full"
          align="center"
        >
          Cancel
        </Button>
        <Button
          keyHint="Enter"
          color="red"
          width="full"
          align="center"
          onClick={async () => {
            await client.call(endpointsMerged.delete, {
              type: schemaType,
              id: itemId,
            })
            Dialog.Close()
            go({ section: normalize(section), id: null })
          }}
        >
          Delete
        </Button>
      </Stack>
    ),
  })

  const typeSchemaRaw: UiSchemaType = useMemo(
    () =>
      schema?.[schemaType || '_root'] ||
      schema?.types?.[schemaType || '']?.props ||
      {},
    [schemaChecksum, schemaType],
  )

  useEffect(() => {
    const locales = schema?.locales
    if (!locales || Object.keys(locales).length <= 1) {
      setIsMultilanguage(false)
      return
    }

    let multi = false

    for (const prop in typeSchemaRaw) {
      if (typeSchemaRaw[prop]?.type === 'text') {
        multi = true
        break
      }
      if (typeSchemaRaw[prop]?.type === 'object') {
        const props = typeSchemaRaw[prop]?.props || {}
        for (const innerProp in props) {
          if (props?.[innerProp]?.type === 'text') {
            multi = true
            break
          }
        }
        if (multi) break
      }
    }

    setIsMultilanguage(multi)
  }, [useDependency(typeSchemaRaw), schemaChecksum])

  const paramsDep = useDependency(params)

  const handleTransformFields = useCallback(
    (fields: any, values: any) => {
      const mergedValues = { ...values }
      for (const key in mergedValues) {
        if ((params as any)[key] !== undefined) {
          mergedValues[key] = (params as any)[key]
        }
      }
      return transformFields?.(fields, mergedValues)
    },
    [transformFields, paramsDep],
  )

  const DirtyWatcher = () => {
    const { isDirty } = useContentEditor()

    useEffect(() => {
      changedFields?.(isDirty)
    }, [isDirty])

    return null
  }

  const BackButton = () => {
    const { isDirty } = useContentEditor()

    if (isBackButtonHidden) return null

    if (!isDirty) {
      return (
        <IconButton
          icon="arrow-left"
          onClick={() => {
            go({ section: normalize(section), id: null }, true)
          }}
          tooltip="Back"
        />
      )
    }

    return (
      <Dialog
        title="Discard changes?"
        description={
          lastUpdateTs
            ? `This ${itemName} was last saved ${prettyDate(
                lastUpdateTs,
                'date-time-human',
              )} ago. All changes since then will be discarded.`
            : 'All your changes will be discarded.'
        }
      >
        <Dialog.Trigger>
          <IconButton icon="arrow-left" tooltip="Back" />
        </Dialog.Trigger>
        <Dialog.Content>
          <Stack gap={12}>
            <Button
              width="full"
              variant="border"
              align="center"
              onClick={() => {
                Dialog.Close()
              }}
            >
              Cancel
            </Button>
            <Button
              width="full"
              keyHint="Enter"
              align="center"
              onClick={() => {
                Dialog.Close()
                go({ section: normalize(section), id: null }, true)
              }}
            >
              Discard
            </Button>
          </Stack>
        </Dialog.Content>
      </Dialog>
    )
  }

  return (
    <Card>
      <ContentEditor
        schemaType={schemaType as any}
        id={itemId}
        customEndpoints={customEndpoints}
        lastUpdatedAt={(ts) => ts && setLastUpdateTs(ts)}
        preview={params.preview ? true : false}
        exclude={exclude}
        include={include}
        disabled={disabled}
        transformFields={handleTransformFields}
        onSubmit={onSubmit}
        onChange={onChange}
      >
        <AppHeader variant="sub">
          <DirtyWatcher />

          <AppHeader.Left>
            {!isBackButtonHidden && <BackButton />}

            <Stack fitContent justify="start" gap={4} style={{ minHeight: 36 }}>
              <Text
                selectableText={false}
                variant="display-medium"
                singleLine
                color="neutral80"
                style={{ padding: '0 8px' }}
              >
                {capitalize(section)}
              </Text>
            </Stack>

            {isMultilanguage &&
              (languageSelector ? languageSelector : <LanguageSelect />)}
          </AppHeader.Left>

          <AppHeader.Right>
            <Stack justify="end" gap={12}>
              {preview && (
                <>
                  <IconButton
                    icon="show"
                    toggled={Boolean(params.preview)}
                    onClick={() => {
                      go({ preview: params.preview ? null : true })
                    }}
                    tooltip="Preview"
                  />
                  {showOpenPreview && (
                    <IconButton
                      icon="arrow-out"
                      onClick={() => {
                        window.open(`/${itemId}`, '_blank')
                      }}
                      tooltip="Open in new tab"
                    />
                  )}
                </>
              )}

              {!onChange && (
                <ContentEditor.Submit>
                  {({ submit, isDirty, changedFields }) => {
                    const fields = changedFields?.length || 0

                    if (isDirty && fields) {
                      const changesPlural = fields === 1 ? 'change' : 'changes'
                      return (
                        <Button style={{ marginLeft: 'auto' }} onClick={submit}>
                          Push {fields} {changesPlural}
                        </Button>
                      )
                    }
                    return null
                  }}
                </ContentEditor.Submit>
              )}

              {trailButton}

              {hideContextMenu ? null : (
                <ContextMenu>
                  <ContextMenu.Item
                    label="Duplicate"
                    leadIcon="copy"
                    onClick={async () => {
                      try {
                        const copiedId = await client.call(
                          endpointsMerged.copy,
                          {
                            type: schemaType,
                            id: itemId,
                          },
                        )

                        go(
                          {
                            section: normalize(section),
                            page: 'edit',
                            id: copiedId,
                          },
                          true,
                        )
                      } catch (err: any) {
                        console.error('‼️ ', err.message || 'Unknown error')
                      }
                    }}
                  />
                  <ContextMenu.Separator />
                  <ContextMenu.Item
                    label="Delete"
                    leadIcon="delete"
                    color="red"
                    onClick={deleteOverlay}
                  />
                </ContextMenu>
              )}
            </Stack>
          </AppHeader.Right>
        </AppHeader>

        <ContentEditor.Header
          customTitle={headerCustomTitle}
          variant={headerVariant}
          style={{
            minWidth: 360,
            width: '100%',
            maxWidth: 600,
            margin: '96px auto',
            marginBottom: banner ? 24 : 96,
            padding: '0px 24px',
          }}
        />

        {banner && (
          <div className="hx h c4 hy hz i0"
          >
            {createElement(banner, {
              schemaType,
              id: itemId,
              section,
              name: itemName,
              icon,
              leadButton,
              counter,
              include,
              exclude,
              customEndpoints,
              onSubmit,
            })}
          </div>
        )}

        <ContentEditor.Fields
          style={{
            minWidth: 360,
            width: '100%',
            maxWidth: 600,
            margin: '0px auto',
            padding: '0px 24px 64px 24px',
          }}
        />

        <ContentEditor.AppPreview>
          {preview &&
            (typeof preview === 'function'
              ? createElement(preview, {
                  schemaType,
                  id: itemId,
                  section,
                  icon,
                  leadButton,
                  counter,
                  include,
                  exclude,
                  customEndpoints,
                  onSubmit,
                })
              : preview)}
        </ContentEditor.AppPreview>
      </ContentEditor>
    </Card>
  )
}
